import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin-only card that shows today's top-10 LLM quota users.  Refreshes
// on demand and every 60 seconds when the card is mounted.
export default function LlmQuotaConsoleCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const _authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchTop = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/llm-quota/top`, {
        credentials: 'include', headers: _authHeaders(),
      });
      if (r.ok) setData(await r.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchTop();
    const iv = setInterval(fetchTop, 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <Card data-testid="admin-quota-console">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-500" /> AI Quota — Top 10 Today
          {data && <Badge variant="outline" className="text-[9px] ml-auto">Limit {data.limit}/day</Badge>}
          <Button size="icon" variant="ghost" onClick={fetchTop} disabled={loading} className="w-6 h-6" data-testid="quota-refresh-btn" title="Refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>
        ) : !data || data.rows.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4" data-testid="quota-empty">No AI usage recorded yet today.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="pb-2 pr-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide">#</th>
                  <th className="pb-2 pr-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide">Name</th>
                  <th className="pb-2 pr-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide">Role</th>
                  <th className="pb-2 pr-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide">Email</th>
                  <th className="pb-2 pr-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide text-right">Used</th>
                  <th className="pb-2 pl-2 font-semibold text-muted-foreground uppercase text-[10px] tracking-wide">Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => {
                  const pct = Math.min(100, (r.count / r.limit) * 100);
                  const barColour = pct >= 100 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
                  return (
                    <tr key={r.user_id} className="border-b border-border/40 hover:bg-muted/30" data-testid={`quota-row-${i}`}>
                      <td className="py-2 pr-2 font-mono text-[10px]">{i + 1}</td>
                      <td className="py-2 pr-2 font-semibold truncate max-w-[200px]">{r.name}</td>
                      <td className="py-2 pr-2 text-muted-foreground text-[10px] uppercase">{r.role.replace('_', ' ')}</td>
                      <td className="py-2 pr-2 text-muted-foreground truncate max-w-[240px]">{r.email}</td>
                      <td className="py-2 pr-2 text-right font-bold">{r.count} / {r.limit}</td>
                      <td className="py-2 pl-2 w-[140px]">
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full ${barColour} transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-2">Resets automatically at midnight UTC · Admin roles are exempt and not shown.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
